import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Table, Badge, Modal, InputGroup, Pagination, Spinner, Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import adminService from '../../../../services/AdminService';
import '../../../../styles/tables.css';

const DistributorConfig = () => {
  const [activeTab, setActiveTab] = useState('portal');
  const [loading, setLoading] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [distributors, setDistributors] = useState([]);
  const [selectedDistributor, setSelectedDistributor] = useState('');
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [configType, setConfigType] = useState('portal');
  const [modalMode, setModalMode] = useState('add');
  const [selectedConfig, setSelectedConfig] = useState(null);
  
  // Separate filters for each tab
  const [filters, setFilters] = useState({
    portal: {
      searchQuery: '',
      currentPage: 1,
      rowsPerPage: 10,
      statusFilter: ''
    },
    upi: {
      searchQuery: '',
      currentPage: 1,
      rowsPerPage: 10,
      statusFilter: ''
    }
  });
  const [configData, setConfigData] = useState({
    portal: {
      operator: '',
      username: '',
      password: '',
      user_details: '',
      full_name: '',
      email: '',
      customer_number: ''
    },
    upi: {
      upi_id: '',
      app_name: '',
      merchant_name: '',
      merchant_code: '',
      api_key: '',
      secret_key: '',
      environment: 'sandbox'
    }
  });

  const [configs, setConfigs] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDistributors();
    fetchConfigs();
  }, [filters[activeTab].currentPage, filters[activeTab].rowsPerPage, filters[activeTab].statusFilter, activeTab]);

  // Debounced search effect
  useEffect(() => {
    if (filters[activeTab].searchQuery === '') return;
    
    const timeoutId = setTimeout(() => {
      if (filters[activeTab].currentPage === 1) {
        fetchConfigs();
      } else {
        updateFilter('currentPage', 1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters[activeTab].searchQuery]);

  const fetchDistributors = async () => {
    try {
      const result = await adminService.getUsers({ role: 'distributor', page: 1, page_size: 1000 });
      if (result.success) {
        setDistributors(result.data.results || []);
      } else {
        console.error('Failed to fetch distributors:', result.error);
      }
    } catch (err) {
      console.error('Error fetching distributors:', err);
    }
  };

  const updateFilter = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [key]: value
      }
    }));
  };

  const fetchConfigs = async () => {
    setLoading(true);
    setError('');
    
    try {
      const currentFilter = filters[activeTab];
      const params = {
        page: currentFilter.currentPage,
        page_size: currentFilter.rowsPerPage
      };

      // Add search parameter
      if (currentFilter.searchQuery.trim()) {
        params.search = currentFilter.searchQuery.trim();
      }

      // Add status filter
      if (currentFilter.statusFilter) {
        params.status = currentFilter.statusFilter;
      }

      let result;
      if (activeTab === 'portal') {
        result = await adminService.getPortalConfigs(params);
      } else if (activeTab === 'upi') {
        result = await adminService.getUpiConfigs(params);
      }

      if (result.success) {
        setConfigs(result.data.results);
        setTotalRecords(result.data.count);
        setTotalPages(result.data.totalPages);
      } else {
        setError(result.error);
        toast.error(result.error);
      }
    } catch (err) {
      setError('Failed to fetch configurations');
      toast.error('Failed to fetch configurations');
    } finally {
      setLoading(false);
    }
  };

  // Data is already filtered and paginated from API
  const getCurrentConfigs = () => {
    return configs;
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDistributor) {
      toast.error('Please select a distributor');
      return;
    }

    setLoading(true);
    
    try {
      let dataToSave;
      
      if (configType === 'upi') {
        // For UPI config, wrap the data in payload field
        dataToSave = {
          distributor: selectedDistributor,
          operator: configData.upi.app_name,
          payload: {
            ...configData.upi
          }
        };
      } else {
        // For other configs, use the data as is
        dataToSave = {
          distributor: selectedDistributor,
          ...configData[configType]
        };
      }

      let result;
      if (modalMode === 'add') {
        if (configType === 'portal') {
          result = await adminService.createPortalConfig(dataToSave);
        } else if (configType === 'upi') {
          result = await adminService.createUpiConfig(dataToSave);
        }
        
        if (result.success) {
          toast.success(`${configType === 'portal' ? 'Portal' : 'UPI'} configuration added successfully`);
          setShowConfigModal(false);
          resetForm();
          fetchConfigs();
        } else {
          toast.error(result.error || 'Failed to create configuration');
        }
      } else {
        if (configType === 'portal') {
          result = await adminService.updatePortalConfig(selectedConfig.id, dataToSave);
        } else if (configType === 'upi') {
          result = await adminService.updateUpiConfig(selectedConfig.id, dataToSave);
        }
        
        if (result.success) {
          toast.success(`${configType === 'portal' ? 'Portal' : 'UPI'} configuration updated successfully`);
          setShowConfigModal(false);
          resetForm();
          fetchConfigs();
        } else {
          toast.error(result.error || 'Failed to update configuration');
        }
      }
    } catch (err) {
      toast.error('An error occurred while saving the configuration');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setConfigData({
      portal: {
        operator: '',
        username: '',
        password: '',
        user_details: '',
        full_name: '',
        email: '',
        customer_number: ''
      },
      upi: {
        upi_id: '',
        app_name: '',
        merchant_name: '',
        merchant_code: '',
        api_key: '',
        secret_key: '',
        environment: 'sandbox'
      }
    });
    setSelectedDistributor('');
    setModalMode('add');
    setSelectedConfig(null);
  };

  const handleConfigChange = (field, value) => {
    setConfigData(prev => ({
      ...prev,
      [configType]: {
        ...prev[configType],
        [field]: value
      }
    }));
  };

  const openConfigModal = (type = null, mode = 'add', config = null) => {
    const targetType = type || activeTab;
    setConfigType(targetType);
    setModalMode(mode);
    setSelectedConfig(config);
    
    if (mode === 'edit' && config) {
      setSelectedDistributor(config.distributor || '');
      if (targetType === 'portal') {
        setConfigData(prev => ({
          ...prev,
          portal: { 
            operator: config.operator || '',
            username: config.username || '',
            password: config.password || '',
            user_details: config.user_details || '',
            full_name: config.full_name || '',
            email: config.email || '',
            customer_number: config.customer_number || ''
          }
        }));
      } else if (targetType === 'upi') {
        setConfigData(prev => ({
          ...prev,
          upi: { 
            upi_id: config.payload?.upi_id || config.upi_id || '',
            app_name: config.payload?.app_name || config.app_name || config.operator || '',
            merchant_name: config.payload?.merchant_name || config.merchant_name || '',
            merchant_code: config.payload?.merchant_code || config.merchant_code || '',
            api_key: config.payload?.api_key || '',
            secret_key: config.payload?.secret_key || '',
            environment: config.payload?.environment || config.environment || 'sandbox'
          }
        }));
      }
    }
    
    setShowConfigModal(true);
  };

  const handleDelete = async (config) => {
    if (window.confirm(`Are you sure you want to delete ${activeTab} configuration for distributor?`)) {
      setLoading(true);
      
      try {
        let result;
        if (activeTab === 'portal') {
          result = await adminService.deletePortalConfig(config.id);
        } else if (activeTab === 'upi') {
          result = await adminService.deleteUpiConfig(config.id);
        }
        
        if (result.success) {
          toast.success('Configuration deleted successfully');
          fetchConfigs();
        } else {
          toast.error(result.error || 'Failed to delete configuration');
        }
      } catch (err) {
        toast.error('An error occurred while deleting the configuration');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleExecuteRequest = async (config) => {
    if (window.confirm(`Execute login request for ${config.operator} portal (${config.username})?`)) {
      setLoading(true);
      
      try {
        // Call execute request API endpoint
        toast.info('Executing login request...');
        
        // TODO: Implement actual API call for executing portal login
        // const result = await adminService.executePortalRequest(config.id);
        
        // Simulate API call for now
        await new Promise(resolve => setTimeout(resolve, 2000));
        
        toast.success(`Login request executed successfully for ${config.operator} portal`);
        
        // Refresh configs to get updated status
        fetchConfigs();
      } catch (err) {
        toast.error('Failed to execute login request');
      } finally {
        setLoading(false);
      }
    }
  };


  const getStatusBadge = (status) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'active') {
      return <Badge bg="success">Active</Badge>;
    } else if (statusLower === 'inactive') {
      return <Badge bg="danger">Inactive</Badge>;
    } else {
      return <Badge bg="secondary">{status}</Badge>;
    }
  };

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ color: '#031a6f', fontWeight: '700' }}>
          <i className="fas fa-cog me-2"></i>
          Distributor Configuration
        </h2>
      </div>

      {/* Tabs */}
      <Tabs
        id="config-tabs"
        activeKey={activeTab}
        onSelect={handleTabChange}
        className="mb-4"
      >
        <Tab 
          eventKey="upi" 
          title={
            <span>
              <i className="fas fa-mobile-alt me-2"></i>
              UPI Configuration
            </span>
          }
        >
          {/* UPI Tab Filters */}
          <Row className="mb-4">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search UPI configurations..."
                  value={filters.upi.searchQuery}
                  onChange={(e) => updateFilter('searchQuery', e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.upi.statusFilter}
                onChange={(e) => updateFilter('statusFilter', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex justify-content-end">
              <Button 
                variant="success" 
                onClick={() => openConfigModal('upi')}
              >
                <i className="fas fa-mobile-alt me-2"></i>
                Add UPI Config
              </Button>
            </Col>
          </Row>

          {/* UPI Configuration Table */}
          <div className="table-responsive">
            <Table striped bordered hover className="modern-table">
              <thead>
                <tr>
                  <th>Actions</th>
                  <th>ID</th>
                  <th>Distributor</th>
                  <th>UPI ID</th>
                  <th>App Name</th>
                  <th>Merchant Name</th>
                  <th>Environment</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Updated</th>
                  
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="text-center py-4">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                      <div className="mt-2">Loading UPI configurations...</div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-danger">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {error}
                    </td>
                  </tr>
                ) : getCurrentConfigs().length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">
                      <i className="fas fa-inbox me-2"></i>
                      No UPI configurations found
                    </td>
                  </tr>
                ) : (
                  getCurrentConfigs().map((config) => (
                    <tr key={config.id}>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openConfigModal('upi', 'edit', config)}
                            disabled={loading}
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(config)}
                            disabled={loading}
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      </td>

                      <td><strong>{config.id}</strong></td>
                      <td>
                        <div>
                          <div><strong>{config.distributor_name || 'N/A'}</strong></div>
                          <small className="text-muted">{config.distributor_shop_name || ''}</small>
                        </div>
                      </td>
                      <td><strong>{config.payload?.upi_id || config.upi_id || 'N/A'}</strong></td>
                      <td>{config.operator || config.payload?.app_name || config.app_name || 'N/A'}</td>
                      <td>{config.payload?.merchant_name || config.merchant_name || 'N/A'}</td>
                      <td>
                        <Badge bg={(config.payload?.environment || config.environment) === 'production' ? 'success' : 'warning'}>
                          {config.payload?.environment || config.environment || 'N/A'}
                        </Badge>
                      </td>
                      <td>{getStatusBadge(config.status)}</td>
                      <td>{new Date(config.created_at).toLocaleDateString()}</td>
                      <td>{new Date(config.updated_at).toLocaleDateString()}</td>
                      
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Tab>

        <Tab 
          eventKey="portal" 
          title={
            <span>
              <i className="fas fa-door-open me-2"></i>
              Portal Login
            </span>
          }
        >
          {/* Portal Tab Filters */}
          <Row className="mb-4">
            <Col md={6}>
              <InputGroup>
                <InputGroup.Text>
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search portal configurations..."
                  value={filters.portal.searchQuery}
                  onChange={(e) => updateFilter('searchQuery', e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <Form.Select
                value={filters.portal.statusFilter}
                onChange={(e) => updateFilter('statusFilter', e.target.value)}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex justify-content-end">
              <Button 
                variant="info" 
                onClick={() => openConfigModal('portal')}
              >
                <i className="fas fa-door-open me-2"></i>
                Add Portal Config
              </Button>
            </Col>
          </Row>

          {/* Portal Configuration Table */}
          <div className="table-responsive">
            <Table striped bordered hover className="modern-table">
              <thead>
                <tr>
                  <th>Actions</th>
                  <th>ID</th>
                  <th>Distributor</th>
                  <th>Operator</th>
                  <th>Username</th>
                  <th>Password</th>
                  <th>Full Name</th>
                  <th>Customer Number</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Updated</th>
                  
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="11" className="text-center py-4">
                      <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </Spinner>
                      <div className="mt-2">Loading portal configurations...</div>
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-danger">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {error}
                    </td>
                  </tr>
                ) : getCurrentConfigs().length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-4 text-muted">
                      <i className="fas fa-inbox me-2"></i>
                      No portal configurations found
                    </td>
                  </tr>
                ) : (
                  getCurrentConfigs().map((config) => (
                    <tr key={config.id}>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleExecuteRequest(config)}
                            disabled={loading}
                            title="Execute Login Request"
                          >
                            <i className="bi bi-play-circle"></i>
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => openConfigModal('portal', 'edit', config)}
                            disabled={loading}
                            title="Edit Configuration"
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(config)}
                            disabled={loading}
                            title="Delete Configuration"
                          >
                            <i className="bi bi-trash"></i>
                          </Button>
                        </div>
                      </td>
                      <td><strong>{config.id}</strong></td>
                      <td>
                        <div>
                          <div><strong>{config.distributor_name || 'N/A'}</strong></div>
                          <small className="text-muted">{config.distributor_mobile || ''}</small>
                        </div>
                      </td>
                      <td>
                        <Badge bg="primary">{config.operator}</Badge>
                      </td>
                      <td><strong>{config.username}</strong></td>
                      <td>
                        {config.password ? (
                          <code className="text-muted small">{config.password}</code>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{config.full_name || 'N/A'}</td>
                      <td>{config.customer_number || 'N/A'}</td>
                      <td>{getStatusBadge(config.status)}</td>
                      <td>{new Date(config.created_at).toLocaleDateString()}</td>
                      <td>{new Date(config.updated_at).toLocaleDateString()}</td>
                      
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Tab>
      </Tabs>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div>
          <Form.Select
            value={filters[activeTab].rowsPerPage}
            onChange={(e) => updateFilter('rowsPerPage', Number(e.target.value))}
            style={{ width: 'auto' }}
          >
            <option value={5}>5 per page</option>
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
          </Form.Select>
        </div>
        <div>
          Showing {totalRecords === 0 ? 0 : ((filters[activeTab].currentPage - 1) * filters[activeTab].rowsPerPage) + 1} to {Math.min(filters[activeTab].currentPage * filters[activeTab].rowsPerPage, totalRecords)} of {totalRecords} entries
        </div>
        <Pagination>
          <Pagination.First 
            onClick={() => updateFilter('currentPage', 1)} 
            disabled={filters[activeTab].currentPage === 1} 
          />
          <Pagination.Prev 
            onClick={() => updateFilter('currentPage', Math.max(filters[activeTab].currentPage - 1, 1))} 
            disabled={filters[activeTab].currentPage === 1} 
          />
          {[...Array(Math.min(5, totalPages))].map((_, index) => {
            const pageNum = Math.max(1, Math.min(filters[activeTab].currentPage - 2 + index, totalPages - 4 + index));
            return (
              <Pagination.Item
                key={pageNum}
                active={pageNum === filters[activeTab].currentPage}
                onClick={() => updateFilter('currentPage', pageNum)}
              >
                {pageNum}
              </Pagination.Item>
            );
          })}
          <Pagination.Next 
            onClick={() => updateFilter('currentPage', Math.min(filters[activeTab].currentPage + 1, totalPages))} 
            disabled={filters[activeTab].currentPage === totalPages} 
          />
          <Pagination.Last 
            onClick={() => updateFilter('currentPage', totalPages)} 
            disabled={filters[activeTab].currentPage === totalPages} 
          />
        </Pagination>
      </div>

      {/* Configuration Form Modal */}
      <Modal show={showConfigModal} onHide={() => setShowConfigModal(false)} size="lg" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className={`bi ${modalMode === 'add' ? 'bi-plus-lg' : 'bi-pencil-square'} me-2`}></i>
            {modalMode === 'add' ? 'Add' : 'Edit'} {configType === 'payment' ? 'Payment Gateway' : configType === 'upi' ? 'UPI' : 'Portal Login'} Configuration
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleConfigSubmit}>
          <Modal.Body>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>
                    Select Distributor <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={selectedDistributor}
                    onChange={(e) => setSelectedDistributor(e.target.value)}
                    required
                  >
                    <option value="">Choose distributor...</option>
                    {distributors.map((dist) => (
                      <option key={dist.id} value={dist.id}>
                        {dist.name} ({dist.mobile})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            {configType === 'payment' ? (
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Gateway Name <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={configData.payment.gateway_name}
                      onChange={(e) => handleConfigChange('gateway_name', e.target.value)}
                      required
                    >
                      <option value="">Select Gateway</option>
                      <option value="Razorpay">Razorpay</option>
                      <option value="PayU">PayU</option>
                      <option value="Paytm">Paytm</option>
                      <option value="CCAvenue">CCAvenue</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Environment <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={configData.payment.environment}
                      onChange={(e) => handleConfigChange('environment', e.target.value)}
                      required
                    >
                      <option value="sandbox">Sandbox</option>
                      <option value="live">Live</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>API Key <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={configData.payment.api_key}
                      onChange={(e) => handleConfigChange('api_key', e.target.value)}
                      placeholder="Enter API key"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Secret Key <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      value={configData.payment.secret_key}
                      onChange={(e) => handleConfigChange('secret_key', e.target.value)}
                      placeholder="Enter secret key"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Merchant ID <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={configData.payment.merchant_id}
                      onChange={(e) => handleConfigChange('merchant_id', e.target.value)}
                      placeholder="Enter merchant ID"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Webhook URL</Form.Label>
                    <Form.Control
                      type="url"
                      value={configData.payment.webhook_url}
                      onChange={(e) => handleConfigChange('webhook_url', e.target.value)}
                      placeholder="https://your-domain.com/webhook"
                    />
                  </Form.Group>
                </Col>
              </Row>
            ) : configType === 'upi' ? (
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>UPI ID <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={configData.upi.upi_id}
                      onChange={(e) => handleConfigChange('upi_id', e.target.value)}
                      placeholder="Enter UPI ID (e.g., merchant@paytm)"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>App Name <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={configData.upi.app_name}
                      onChange={(e) => handleConfigChange('app_name', e.target.value)}
                      required
                    >
                      <option value="">Select App</option>
                      <option value="paytm">Paytm</option>
                      <option value="phonepe">PhonePe</option>
                      <option value="googlepay">Google Pay</option>
                      <option value="razorpay">Razorpay</option>
                      <option value="bharatpe">BharatPe</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Merchant Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={configData.upi.merchant_name}
                      onChange={(e) => handleConfigChange('merchant_name', e.target.value)}
                      placeholder="Enter merchant name"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Merchant Code <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={configData.upi.merchant_code}
                      onChange={(e) => handleConfigChange('merchant_code', e.target.value)}
                      placeholder="Enter merchant code"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>API Key <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={configData.upi.api_key}
                      onChange={(e) => handleConfigChange('api_key', e.target.value)}
                      placeholder="Enter API key"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Secret Key <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      value={configData.upi.secret_key}
                      onChange={(e) => handleConfigChange('secret_key', e.target.value)}
                      placeholder="Enter secret key"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Environment <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={configData.upi.environment}
                      onChange={(e) => handleConfigChange('environment', e.target.value)}
                      required
                    >
                      <option value="sandbox">Sandbox</option>
                      <option value="production">Production</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            ) : (
              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Operator <span className="text-danger">*</span></Form.Label>
                    <Form.Select
                      value={configData.portal.operator}
                      onChange={(e) => handleConfigChange('operator', e.target.value)}
                      required
                    >
                      <option value="">Select Operator</option>
                      <option value="jio">Jio</option>
                      <option value="airtel">Airtel</option>
                      <option value="vodafone">Vodafone</option>
                      <option value="bsnl">BSNL</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Username <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={configData.portal.username}
                      onChange={(e) => handleConfigChange('username', e.target.value)}
                      placeholder="Enter username"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Password <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="password"
                      value={configData.portal.password}
                      onChange={(e) => handleConfigChange('password', e.target.value)}
                      placeholder="Enter password"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>User Details</Form.Label>
                    <Form.Control
                      type="text"
                      value={configData.portal.user_details}
                      onChange={(e) => handleConfigChange('user_details', e.target.value)}
                      placeholder="User details code"
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                      type="text"
                      value={configData.portal.full_name}
                      onChange={(e) => handleConfigChange('full_name', e.target.value)}
                      placeholder="Enter full name"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={configData.portal.email}
                      onChange={(e) => handleConfigChange('email', e.target.value)}
                      placeholder="Enter email address"
                    />
                  </Form.Group>
                </Col>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>Customer Number</Form.Label>
                    <Form.Control
                      type="text"
                      value={configData.portal.customer_number}
                      onChange={(e) => handleConfigChange('customer_number', e.target.value)}
                      placeholder="Enter customer number"
                    />
                  </Form.Group>
                </Col>
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer style={{ borderTop: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
            <Button 
              type="submit"
              className="btn-outline-primary-custom"
              disabled={loading}
            >
              {loading ? 'Saving...' : (modalMode === 'add' ? 'Add Configuration' : 'Update Configuration')}
            </Button>
            <Button 
              variant="secondary" 
              onClick={() => setShowConfigModal(false)}
              className="btn-outline-danger-custom"
            >
              Cancel
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default DistributorConfig;